import type { LatLngInput } from 'graphql/generated/graphql';
import isRecord from 'utils/isRecord';
import requireValue from 'utils/requireValue';

// The calculator pages send the wizard values as they are. The mutation inputs require some of
// them (and type the selects as enums): the yup schemas of the steps require these fields before
// a step is submitted, and the selects only offer the enum values, so the conversions (`./inputs`, `toCO2CalculatorInput`) return
// the values unchanged. The throws are the requests the server rejected, which the untyped code
// sent; they are caught and shown as the error toast as the rejections were.

/** The wizard coordinates (picked from the project or set by the address autocomplete). */
export interface IFormCoordinates {
  lat?: number | null;
  lng?: number | null;
}

/** A member of a string enum of the GraphQL schema. */
export const requireEnum = <TEnum extends string>(
  enumObject: Record<string, TEnum>,
  value: unknown,
  property: string,
): TEnum => {
  const member = Object.values(enumObject).find((enumValue) => enumValue === value);
  if (member === undefined) {
    throw new TypeError(`Unknown ${property}: ${String(value)}`);
  }
  return member;
};

/** A member of a string enum, or the null or undefined of an optional field as it was. */
export const optionalEnum = <TEnum extends string>(
  enumObject: Record<string, TEnum>,
  value: unknown,
  property: string,
): TEnum | null | undefined => {
  if (value === null || value === undefined) return value;
  return requireEnum(enumObject, value, property);
};

/** The coordinates with both values (an address is chosen with its position), or none. */
export const toLatLngInput = (
  coordinates: IFormCoordinates | null | undefined,
): LatLngInput | null | undefined => {
  if (coordinates === null || coordinates === undefined) return coordinates;
  return {
    ...coordinates,
    lat: requireValue(coordinates.lat, 'lat'),
    lng: requireValue(coordinates.lng, 'lng'),
  };
};

/** The finite numbers of a list (the living space of the floors after `compact`). */
export const requireNumbers = (values: unknown[], property: string): number[] =>
  values.map((value) => {
    if (typeof value !== 'number') {
      throw new TypeError(`Invalid ${property}: ${String(value)}`);
    }
    return value;
  });

/** The `raw` the mutation hooks attach to the error they throw (`withRaw`). */
export const rawOf = (error: unknown): unknown => (isRecord(error) ? error.raw : undefined);

/** The checked features (`features.<name>`) as the object of the wizard. */
export const toFeatureFlags = (features: ReadonlyArray<string | null>) =>
  // String(): a computed key is the string of the name, as before.
  features.reduce<Record<string, boolean>>((acc, name) => ({ ...acc, [String(name)]: true }), {});

/** `!!form_values?.other_investments_amount` (the form values of some project types have none). */
export const hasOtherInvestmentsAmount = (formValues: object | null | undefined) =>
  isRecord(formValues) && !!formValues.other_investments_amount;

/**
 * The wizard step to save: none in edit mode, else the next or the current one (`changeStep` is
 * never passed by the steps, so it is the next one).
 */
export const getStep = (
  isEditMode: boolean | undefined,
  changeStep: boolean,
  currentStep: number | null | undefined,
) => {
  if (isEditMode) return null;
  // Number(): `currentStep + 1` as before (StepWizard always passes currentStep; null -> 1).
  if (changeStep) return Number(currentStep) + 1;
  return currentStep;
};
